import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/feature/StructuredData';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.naishuimanhua.com/' },
          ...(category ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `https://www.naishuimanhua.com/category/${category.slug}` }] : []),
          { '@type': 'ListItem', position: category ? 3 : 2, name: article.title, item: `https://www.naishuimanhua.com/article/${article.slug}` },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.coverImage,
        datePublished: article.publishDate,
        author: { '@type': 'Organization', name: article.author },
        publisher: { '@type': 'Organization', name: '奶水漫画' },
        about: article.tags.map((tag) => ({ '@type': 'Thing', name: tag })),
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.naishuimanhua.com/article/${article.slug}` },
      },
    ],
  };

  return (
    <>
      <StructuredData data={structuredData} />
      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              ...(category ? [{ label: category.name, path: `/category/${category.slug}` }] : []),
              { label: article.title },
            ]}
          />

          <div className="py-8">
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 text-xs rounded-full bg-accent-100 text-accent-700">
                    {tag}
                  </span>
                ))}
                {article.featured && (
                  <span className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700">
                    精选推荐
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line text-xs"></i>
                  </span>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-xs"></i>
                  </span>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-xs"></i>
                  </span>
                  {article.readTime} 分钟阅读
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line text-xs"></i>
                  </span>
                  {article.viewCount.toLocaleString()} 次阅读
                </span>
              </div>
            </header>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 奶水漫画`}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>

            <div className="prose-custom">
              {article.content.map((block, index) => {
                switch (block.type) {
                  case 'paragraph':
                    return (
                      <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                        {block.value}
                      </p>
                    );
                  case 'subtitle':
                    return (
                      <h2 key={index} className="text-lg md:text-xl font-semibold text-foreground-950 mt-8 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                        {block.value}
                      </h2>
                    );
                  case 'image':
                    return (
                      <figure key={index} className="my-6">
                        <div className="rounded-lg overflow-hidden">
                          <img
                            src={block.value}
                            alt={block.caption || ''}
                            title={block.caption ? `${block.caption} - 奶水漫画` : `奶水漫画插图`}
                            className="w-full h-auto"
                            loading="lazy"
                          />
                        </div>
                        {block.caption && (
                          <figcaption className="text-center text-xs text-foreground-500 mt-2">
                            {block.caption}
                          </figcaption>
                        )}
                      </figure>
                    );
                  case 'tip':
                    return (
                      <div key={index} className="my-6 p-4 rounded-lg border-l-4 bg-accent-100/50" style={{ borderLeftColor: 'oklch(var(--accent-500))' }}>
                        <p className="text-sm text-foreground-700 leading-relaxed">
                          {block.value}
                        </p>
                      </div>
                    );
                  default:
                    return null;
                }
              })}
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/60">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-600">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-700 border border-background-200/60">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-10 border-t border-background-200/60">
            <h3 className="text-lg font-semibold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              相关推荐
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-accent-200 transition-all duration-300 no-underline"
                >
                  <div className="relative w-full h-[140px] overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      title={`${ra.title} - 奶水漫画`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-accent-600 transition-colors">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-1">{ra.publishDate}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}